import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/base/SEO';

export default function ContactPage() {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://braincommerce.com';
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    inquiryType: '',
    message: ''
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact BrainCommerce",
    "description": "Get in touch with BrainCommerce. Contact us for enterprise solutions, partnerships, or general inquiries.",
    "url": `${siteUrl}/contact`
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert(t('contact.form.success'));
  };

  return (
    <>
      <SEO
        title="Contact BrainCommerce - Get in Touch for Workforce Solutions"
        description="Get in touch with BrainCommerce for enterprise solutions, partnerships, or general inquiries. Offices in Seoul and Singapore. Email: contact@braincommerce.com"
        keywords="contact BrainCommerce, get in touch, enterprise inquiries, partnership opportunities, BrainCommerce offices"
        canonical={`${siteUrl}/contact`}
        schema={schema}
      />
      
      <div className="min-h-screen flex flex-col md:flex-row">
        {/* Left Panel - Dark */}
        <div className="w-full md:w-1/2 bg-[#0a0a0a] text-white flex flex-col justify-between p-6 md:p-12 lg:p-20 pt-24 md:pt-12 lg:pt-20">
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6 md:mb-8 leading-tight">
              {t('contact.left.title')}
            </h1>
            <p className="text-base md:text-lg text-gray-400 mb-12 md:mb-16">
              {t('contact.left.subtitle')}
            </p>
            
            <div className="space-y-6 md:space-y-8 mb-12 md:mb-16">
              <div>
                <h3 className="text-xs md:text-sm uppercase tracking-wider text-gray-500 mb-2">{t('contact.left.email')}</h3>
                <a href="mailto:contact@braincommerce.com" className="text-lg md:text-xl hover:text-gray-300 transition-colors cursor-pointer break-all">
                  contact@braincommerce.com
                </a>
              </div>
              
              <div>
                <h3 className="text-xs md:text-sm uppercase tracking-wider text-gray-500 mb-2">{t('contact.left.phone')}</h3>
                <a href="tel:+82234567890" className="text-lg md:text-xl hover:text-gray-300 transition-colors cursor-pointer">
                  +82 2-3456-7890
                </a>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-xs md:text-sm uppercase tracking-wider text-gray-500 mb-4 md:mb-6">{t('contact.left.offices')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8">
              <div>
                <h4 className="text-base md:text-lg mb-2">{t('contact.left.seoul.title')}</h4>
                <p className="text-xs md:text-sm text-gray-400 leading-relaxed">
                  {t('contact.left.seoul.address')}
                </p>
              </div>
              <div>
                <h4 className="text-base md:text-lg mb-2">{t('contact.left.singapore.title')}</h4>
                <p className="text-xs md:text-sm text-gray-400 leading-relaxed">
                  {t('contact.left.singapore.address')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - White Form */}
        <div className="w-full md:w-1/2 bg-white flex items-center justify-center p-6 md:p-12 lg:p-20">
          <form onSubmit={handleSubmit} className="w-full max-w-[480px]">
            <div className="space-y-6 md:space-y-8">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#4a4a4a] mb-2">
                  {t('contact.form.fullName')}
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full pb-3 border-b border-gray-300 focus:border-[#1a1a1a] outline-none transition-colors text-base md:text-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#4a4a4a] mb-2">
                  {t('contact.form.company')}
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full pb-3 border-b border-gray-300 focus:border-[#1a1a1a] outline-none transition-colors text-base md:text-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#4a4a4a] mb-2">
                  {t('contact.form.email')}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pb-3 border-b border-gray-300 focus:border-[#1a1a1a] outline-none transition-colors text-base md:text-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#4a4a4a] mb-2">
                  {t('contact.form.phone')}
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full pb-3 border-b border-gray-300 focus:border-[#1a1a1a] outline-none transition-colors text-base md:text-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#4a4a4a] mb-2">
                  {t('contact.form.inquiryType')}
                </label>
                <select
                  value={formData.inquiryType}
                  onChange={(e) => setFormData({ ...formData, inquiryType: e.target.value })}
                  className="w-full pb-3 border-b border-gray-300 focus:border-[#1a1a1a] outline-none transition-colors text-base md:text-lg cursor-pointer"
                  required
                >
                  <option value="">{t('contact.form.selectType')}</option>
                  <option value="enterprise">{t('contact.form.types.enterprise')}</option>
                  <option value="partnership">{t('contact.form.types.partnership')}</option>
                  <option value="careers">{t('contact.form.types.careers')}</option>
                  <option value="media">{t('contact.form.types.media')}</option>
                  <option value="other">{t('contact.form.types.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-[#4a4a4a] mb-2">
                  {t('contact.form.message')}
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full pb-3 border-b border-gray-300 focus:border-[#1a1a1a] outline-none transition-colors text-base md:text-lg resize-none"
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full py-3 md:py-4 bg-[#1a1a1a] text-white rounded-lg hover:bg-[#2a2a2a] transition-colors cursor-pointer whitespace-nowrap text-base md:text-lg"
              >
                {t('contact.form.submit')}
              </button>

              <p className="text-xs text-[#4a4a4a] text-center">
                {t('contact.form.privacy')}
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
